import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEvents, createEvent, updateEventStatus, seedDemoData } from '../utils/api';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import CreateEventDialog from '../components/dashboard/CreateEventDialog';
import EventCard from '../components/dashboard/EventCard';
import EmptyState from '../components/dashboard/EmptyState';
import LoadingScreen from '../components/ui/LoadingScreen';
import QuotaWidget from '../components/dashboard/QuotaWidget';

const Dashboard = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    location: ''
  });

  useEffect(() => {
    loadEvents();
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await getEvents();
      setEvents(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Erreur lors du chargement des événements');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await createEvent(formData);
      toast.success('Événement créé avec succès !');
      setDialogOpen(false);
      setFormData({ name: '', date: '', location: '' });
      loadEvents();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleCreateDemo = async () => {
    try {
      await seedDemoData();
      toast.success('Événement démo créé !');
      loadEvents();
    } catch (error) {
      toast.error('Erreur lors de la création de la démo');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleUpgradeClick = () => {
    toast.info('Le plan Pro sera bientôt disponible ! Restez connecté.', {
      duration: 5000,
    });
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await updateEventStatus(eventId, newStatus);
      toast.success(`Événement ${newStatus === 'live' ? 'démarré' : 'terminé'} !`);
      loadEvents();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Événement supprimé avec succès !');
      loadEvents();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    }
  };

  const handleArchiveEvent = async (eventId) => {
    try {
      await api.patch(`/events/${eventId}/archive`);
      toast.success('Événement archivé avec succès !');
      loadEvents();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'archivage";
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return <LoadingScreen message="Chargement des événements..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <DashboardHeader onLogout={handleLogout} currentUser={currentUser} />

        {/* Quota Widget */}
        <div className="mb-6">
          <QuotaWidget onUpgradeClick={handleUpgradeClick} />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
          <CreateEventDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            formData={formData}
            onFormChange={setFormData}
            onSubmit={handleCreateEvent}
          />

          <Button
            data-testid="create-demo-button"
            onClick={handleCreateDemo}
            variant="outline"
            className="border-violet-300 text-violet-600 hover:bg-violet-50 hover:border-violet-400 hover:text-violet-700 h-10 sm:h-12 transition-all duration-200 text-sm sm:text-base"
          >
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            <span className="hidden sm:inline">Créer un événement démo</span>
            <span className="sm:hidden">Événement démo</span>
          </Button>
        </div>

        {/* Events Grid */}
        {events.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onStatusChange={handleStatusChange}
                onNavigate={navigate}
                onDelete={handleDeleteEvent}
                onArchive={handleArchiveEvent}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
